import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import {
  countryCodes,
  isSameCountryCode,
} from "@/react/utils/paywalls/countryCodes";
import {
  getBrazilCpfTaxIdType,
  getTaxIdTypeByLabel,
  mapTaxIdTypeToOptions,
} from "@/react/utils/paywalls/taxId/helpers";
import { taxIdTypes } from "@/react/utils/paywalls/taxId/taxIdTypes";

export interface UseTaxIdFormConfig {
  fieldNames: {
    addressCountry: string;
    taxIdType: string;
    taxIdTypeLabel: string;
    taxIdValue: string;
  };
  isBrazilianCommunity: boolean;
  isTaxCollectionEnabled: boolean;
  selectedCountry?: string;
}

export const useTaxIdForm = ({
  fieldNames,
  isBrazilianCommunity,
  isTaxCollectionEnabled,
  selectedCountry,
}: UseTaxIdFormConfig) => {
  const {
    taxIdType: taxIdTypeFieldName,
    taxIdTypeLabel: taxIdTypeLabelFieldName,
    taxIdValue: taxIdValueFieldName,
  } = fieldNames;

  const { setValue, watch } = useFormContext<Record<string, string>>();

  // Watch the appropriate tax ID type field (label for profile, type for checkout)
  const taxIdTypeLabel = watch(taxIdTypeLabelFieldName);
  const selectedTaxIdType = getTaxIdTypeByLabel(taxIdTypeLabel);

  // Tax ID is required when Brazil is selected
  const isRequired = isSameCountryCode(selectedCountry, countryCodes.br);

  // Filter tax ID types based on selected country
  const applicableTaxIdTypes = useMemo(() => {
    if (!selectedCountry) return taxIdTypes; // Show all tax ID types when country is not set

    return taxIdTypes.filter(type =>
      isSameCountryCode(type.countryCode, selectedCountry),
    );
  }, [selectedCountry]);

  // Get tax ID type options for select dropdown
  const taxIdTypeOptions = useMemo(
    () => applicableTaxIdTypes.map(mapTaxIdTypeToOptions),
    [applicableTaxIdTypes],
  );

  // Tax ID fields are visible for:
  // - All Brazilian communities
  // - Non-Brazilian communities with tax collection enabled
  //
  // that have tax ID types available
  const isVisible = useMemo(
    () =>
      (isBrazilianCommunity || isTaxCollectionEnabled) &&
      applicableTaxIdTypes.length > 0,
    [isBrazilianCommunity, isTaxCollectionEnabled, applicableTaxIdTypes],
  );

  // Options with conditional clear (only when not required)
  const selectOptions = useMemo(() => {
    const options = [...taxIdTypeOptions];

    // Add clear option when allowed AND not required
    if (!isRequired) {
      options.unshift({
        value: "",
        text: t("not_applicable"),
      });
    }

    return options;
  }, [taxIdTypeOptions, isRequired]);

  // Effect 1: Set form values when tax ID type changes
  useEffect(() => {
    if (selectedTaxIdType) {
      setValue(taxIdTypeFieldName, selectedTaxIdType.enum);
    } else {
      setValue(taxIdTypeFieldName, "");
      setValue(taxIdValueFieldName, "");
    }
  }, [selectedTaxIdType, setValue, taxIdTypeFieldName, taxIdValueFieldName]);

  // Effect 2: Reset tax ID when country changes and doesn't match current selection
  useEffect(() => {
    if (
      selectedCountry &&
      selectedTaxIdType &&
      !isSameCountryCode(selectedTaxIdType.countryCode, selectedCountry)
    ) {
      setValue(taxIdTypeLabelFieldName, "");
    }
  }, [selectedCountry, selectedTaxIdType, setValue, taxIdTypeLabelFieldName]);

  // Effect 3: Pre-fill Brazilian CPF when mandatory for Brazilian communities
  useEffect(() => {
    // Only pre-fill if:
    // 1. It's a Brazilian community AND
    // 2. Tax ID is required AND
    // 3. No tax ID type is currently selected
    if (isBrazilianCommunity && isRequired && !taxIdTypeLabel) {
      const brazilCpfTaxIdType = getBrazilCpfTaxIdType();
      if (brazilCpfTaxIdType) {
        setValue(taxIdTypeLabelFieldName, brazilCpfTaxIdType.label, {
          shouldDirty: true,
        });
      }
    }
  }, [
    isBrazilianCommunity,
    setValue,
    isRequired,
    taxIdTypeLabelFieldName,
    taxIdTypeLabel,
  ]);

  const handleTaxIdTypeChange = useCallback(
    ({ value }: { value: string }) => {
      setValue(taxIdTypeLabelFieldName, value, { shouldDirty: true });
    },
    [setValue, taxIdTypeLabelFieldName],
  );

  // Tax ID value is required if:
  // 1. Country requires it (Brazil + Brazilian community/tax collection), OR
  // 2. User has selected a tax ID type
  const isTaxIdValueRequired = isRequired || Boolean(selectedTaxIdType);

  return {
    handleTaxIdTypeChange,
    isRequired,
    isTaxIdValueRequired,
    isVisible,
    selectedTaxIdType,
    selectOptions,
    taxIdTypeLabel,
  };
};
